import classNames from 'classnames';
import { AlertCircle, ChevronRight } from 'lucide-react';
import { useContext, memo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DateRange } from 'react-day-picker';
import Table from '../components/Table';
import { GlobalContext } from '../context/GlobalContext';
import useFetch from '../hooks/api';
import { UserData } from '../hooks/auth';
import { DateRangePicker } from '../shadcn/DateRangePicker';
import Select from '../components/Select';
import StoreHealthCard from '../components/StoreHealthCard';

function currency(amount: number, currencySymbol: string) {
	return (
		currencySymbol +
		amount.toLocaleString('en-US', {
			maximumFractionDigits: 2,
			minimumFractionDigits: 2
		})
	);
}

const SummaryTables = memo(({ userData, internal, selectedBranch }: { userData: UserData; internal: boolean; selectedBranch: string }) => (
	<div className="grid grid-cols-1 gap-4 light:gap-8">
		<section className="w-full space-y-4">
			<Link to="/reportes/ventas">
				<header className="group flex cursor-pointer items-center justify-between">
					<h2 className="text-xl font-medium tracking-tight transition-colors group-hover:text-primary-400 3xl:text-2xl">
						Ventas
					</h2>
					<ChevronRight
						strokeWidth={2.25}
						className="size-6 text-slate-200 transition-all group-hover:translate-x-[2px] group-hover:text-primary-400 3xl:size-8"
					/>
				</header>
			</Link>
			<Table
				key={`devices-${selectedBranch}`}
				userEmail={userData.master_store.email || userData.master_store.id}
				endpoint="https://api.krece.app/tables/devices/"
				pagination={{ default: 10, batchSizes: [10] }}
				orderBy={{ columnName: 'Fecha de Compra', columnOrder: 'DESC' }}
				hideDateFilters={true}
				hideExport={true}
				hidePagination={true}
				className="!h-[400px]"
				filter_by={selectedBranch !== 'all' ? [{ column_name: 'Sucursal', value: selectedBranch }] : undefined}
			/>
		</section>
		<section className="w-full space-y-4">
			<Link to="/reportes/transacciones">
				<header className="group flex cursor-pointer items-center justify-between">
					<h2 className="text-xl font-medium tracking-tight transition-colors group-hover:text-primary-400 3xl:text-2xl">
						Transacciones
					</h2>
					<ChevronRight
						strokeWidth={2.25}
						className="size-6 text-slate-200 transition-all group-hover:translate-x-[2px] group-hover:text-primary-400 3xl:size-8"
					/>
				</header>
			</Link>
			<Table
				key={`transactions-${selectedBranch}`}
				userEmail={userData.master_store.email || userData.master_store.id}
				endpoint="https://api.krece.app/tables/transactions/"
				pagination={{ default: 10, batchSizes: [10] }}
				orderBy={{ columnName: 'Fecha Reportado', columnOrder: 'DESC' }}
				hideDateFilters={true}
				hideExport={true}
				hidePagination={true}
				className="!h-[400px]"
				filter_by={(() => {
					const filters: { column_name: string; value: string }[] = [];
					if (selectedBranch !== 'all') filters.push({ column_name: 'Sucursal', value: selectedBranch });
					const shouldFilterDivisas = !(!userData?.master_user && !userData?.sub_stores && userData?.store_name && !internal) && userData?.financed_by_krece;
					if (shouldFilterDivisas) filters.push({ column_name: 'Tipo de operación', value: 'Divisas en tienda' });
					return filters.length > 0 ? filters : undefined;
				})()}
			/>
		</section>
	</div>
));

type CustomRangeOption = 'hoy' | 'ayer' | 'antiayer' | 'semana actual' | 'semana anterior' | 'corte actual' | 'corte anterior';

const CUSTOM_RANGE_OPTIONS: { value: CustomRangeOption; label: string }[] = [
	{ value: 'hoy', label: 'Hoy' },
	{ value: 'ayer', label: 'Ayer' },
	{ value: 'antiayer', label: 'Anteayer' },
	{ value: 'semana actual', label: 'Semana Actual' },
	{ value: 'semana anterior', label: 'Semana Anterior' },
	{ value: 'corte actual', label: 'Corte Actual' },
	{ value: 'corte anterior', label: 'Corte Anterior' }
];

export default function Inicio() {
	const { userData, testAccounts, CountrySettings, internal } = useContext(GlobalContext);
	const { master_store, store_name, master_user, email } = userData!;
	const isInactive = userData?.is_active === false || userData?.master_store?.is_active === false;

	const currencySymbol = CountrySettings?.currency_code || '$';

	const storeEmail = userData?.master_store?.email || userData?.master_store?.id || userData?.email;
	const showStoreHealth = !testAccounts.includes(email);
	const { data: storeHealthSummary, loading: storeHealthLoading, error: storeHealthError } = useFetch(
		'https://api.krece.app/storeprofile/store_health_summary/',
		'GET',
		!!storeEmail && showStoreHealth,
		undefined,
		undefined,
		true
	);

	const [selectedCustomRange, setSelectedCustomRange] = useState<CustomRangeOption>('hoy');
	const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
	const [filterMode, setFilterMode] = useState<'custom' | 'date'>('custom');
	const [selectedBranch, setSelectedBranch] = useState<string>('all');

	const branchOptions = master_user && userData?.sub_stores
		? [{ label: 'Todas', value: 'all' }, ...userData.sub_stores.map((branch) => ({ label: branch, value: branch }))]
		: [];

	const { data, loading, setPayload } = useFetch('https://api.krece.app/tables/storesummarydata/', 'POST', true, {
		master_store_email: master_store.id,
		...(master_user === false && { filter_by: [{ column_name: 'sub_store', value: store_name }] }),
		date_range: { custom_range: 'hoy' }
	} as any);

	useEffect(() => {
		const newPayload: any = {
			master_store_email: master_store.id
		};

		// For non-master users, always filter by their store
		if (master_user === false) {
			newPayload.filter_by = [{ column_name: 'sub_store', value: store_name }];
		} else if (selectedBranch !== 'all') {
			// For master users, filter by selected branch if not "all"
			newPayload.filter_by = [{ column_name: 'sub_store', value: selectedBranch }];
		}

		if (filterMode === 'custom') {
			newPayload.date_range = { custom_range: selectedCustomRange };
		} else if (filterMode === 'date' && dateRange?.from && dateRange?.to) {
			newPayload.date_range = {
				from_date: dateRange.from.toISOString().split('T')[0],
				to_date: dateRange.to.toISOString().split('T')[0]
			};
		} else {
			// Fallback to 'hoy' if date range is incomplete
			newPayload.date_range = { custom_range: 'hoy' };
		}

		setPayload(newPayload);
	}, [filterMode, selectedCustomRange, dateRange, master_store.id, master_user, store_name, selectedBranch, setPayload]);

	const [updateRegistrationSteps, setUpdateRegistrationSteps] = useState(false);
	const { setPayload: setUpdateRegistrationStepsPayload } = useFetch(
		'https://api.krece.app/storeprofile/update_registration_steps/',
		'POST',
		updateRegistrationSteps,
		undefined
	);


	const handleCustomRangeChange = (range: CustomRangeOption) => {
		setSelectedCustomRange(range);
		setFilterMode('custom');
		setDateRange(undefined);
	};

	const handleDateRangeChange = (range: DateRange | undefined) => {
		setDateRange(range);
		if (range?.from && range?.to) {
			setFilterMode('date');
		}
	};

	// Filter out corte options for DR
	const filteredRangeOptions = userData?.country === 'rd'
		? CUSTOM_RANGE_OPTIONS.filter(option => option.value !== 'corte actual' && option.value !== 'corte anterior')
		: CUSTOM_RANGE_OPTIONS;

	const FilterBar = () => (
		<div className="flex flex-col gap-3">
			{/* Quick Range Buttons - Desktop */}
			<div className="no-scrollbar hidden w-full gap-1 rounded-xl border border-neutral-800/50 bg-neutral-900/50 p-1 backdrop-blur-sm lg:flex">
				{filteredRangeOptions.map((option) => (
					<button
						key={option.value}
						onClick={() => handleCustomRangeChange(option.value)}
						disabled={loading}
						className={classNames(
							'group relative flex-1 whitespace-nowrap rounded-lg px-3 py-2 text-center text-xs font-medium transition-all duration-200',
							filterMode === 'custom' && selectedCustomRange === option.value
								? 'bg-primary-500 text-white shadow-lg shadow-primary-500/25'
								: 'text-neutral-400 hover:text-primary-400 light:hover:text-primary-600',
							loading && 'cursor-not-allowed opacity-50'
						)}
					>
						{option.label}
					</button>
				))}
			</div>
			{/* Quick Range Select - Mobile */}
			<div className="w-full lg:hidden">
				<Select
					options={filteredRangeOptions}
					defaultValue={selectedCustomRange}
					onSelectedChange={(selected) => {
						if (selected && typeof selected === 'string') {
							handleCustomRangeChange(selected as CustomRangeOption);
						}
					}}
					placeholder="Seleccionar rango"
					floatingLabel="Rango rápido"
					containerClassName="w-full"
					className="w-full"
					disabled={loading}
				/>
			</div>
			{/* Branch + Date row */}
			<div className="flex flex-col gap-2.5 sm:flex-row sm:items-center">
				{master_user && branchOptions.length > 0 && (
					<Select
						options={branchOptions}
						defaultValue={selectedBranch}
						onSelectedChange={(selected) => {
							if (selected && typeof selected === 'string') {
								setSelectedBranch(selected);
							}
						}}
						placeholder="Seleccionar sucursal"
						floatingLabel="Sucursal"
						containerClassName="w-full sm:flex-1"
						disabled={loading}
					/>
				)}
				<div className="w-full sm:flex-1">
					<DateRangePicker
						onRangeChange={handleDateRangeChange}
						defaultValue={dateRange}
						floatingLabel="Rango personalizado"
						className="w-full"
						disabled={loading}
					/>
				</div>
			</div>
		</div>
	);

	const AnalyticsCards = () => (
		<section className="no-scrollbar -mx-4 flex select-none gap-3.5 overflow-x-auto px-4 md:mx-0 md:px-0 lg:gap-4">
			{/* Ventas Card */}
			<article className="relative min-w-[280px] shrink-0 overflow-hidden rounded-2xl border border-neutral-800/50 bg-gradient-to-br from-neutral-900/90 to-neutral-925/90 p-5 backdrop-blur-sm md:min-w-0 md:flex-1 3xl:p-7">
				<div className="space-y-3 3xl:space-y-4">
					<div className="flex size-10 items-center justify-center rounded-xl bg-primary-500/10 3xl:size-12 light:bg-primary-500/20">
						<svg
							className="size-5 text-primary-400 3xl:size-6 light:text-primary-600"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
							strokeWidth={2}
						>
							<path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
						</svg>
					</div>
					<div className="space-y-1">
						<h3 className="text-xs font-medium uppercase tracking-wider text-neutral-400 3xl:text-sm">
							Ventas
						</h3>
						<p className="text-3xl font-bold tracking-tight text-neutral-50 3xl:text-4xl">
							{data?.[0]?.ventas || 0}
						</p>
					</div>
				</div>
			</article>

			{/* Monto Financiado Card */}
			<article className="relative min-w-[280px] shrink-0 overflow-hidden rounded-2xl border border-neutral-800/50 bg-gradient-to-br from-neutral-900/90 to-neutral-925/90 p-5 backdrop-blur-sm md:min-w-0 md:flex-1 3xl:p-7">
				<div className="space-y-3 3xl:space-y-4">
					<div className="flex size-10 items-center justify-center rounded-xl bg-primary-500/10 3xl:size-12 light:bg-primary-500/20">
						<svg
							className="size-5 text-primary-400 3xl:size-6 light:text-primary-600"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
							strokeWidth={2}
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
							/>
						</svg>
					</div>
					<div className="space-y-1">
						<h3 className="text-xs font-medium uppercase tracking-wider text-neutral-400 3xl:text-sm">
							Monto Financiado
						</h3>
						<p className="text-3xl font-bold tracking-tight text-neutral-50 3xl:text-4xl">
							{currency(data?.[0]?.monto_financiado || 0, currencySymbol)}
						</p>
					</div>
				</div>
			</article>

			{/* Pagos en Tienda Card */}
			<article className="relative min-w-[280px] shrink-0 overflow-hidden rounded-2xl border border-neutral-800/50 bg-gradient-to-br from-neutral-900/90 to-neutral-925/90 p-5 backdrop-blur-sm md:min-w-0 md:flex-1 3xl:p-7">
				<div className="space-y-3 3xl:space-y-4">
					<div className="flex size-10 items-center justify-center rounded-xl bg-primary-500/10 3xl:size-12 light:bg-primary-500/20">
						<svg
							className="size-5 text-primary-400 3xl:size-6 light:text-primary-600"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
							strokeWidth={2}
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
							/>
						</svg>
					</div>
					<div className="space-y-1">
						<h3 className="text-xs font-medium uppercase tracking-wider text-neutral-400 3xl:text-sm">
							Pagos en Tienda
						</h3>
						<p className="text-3xl font-bold tracking-tight text-neutral-50 3xl:text-4xl">
							{currency(data?.[0]?.pagos_en_tienda || 0, currencySymbol)}
						</p>
					</div>
				</div>
			</article>
		</section>
	);

	const AnalyticsCardsPlaceholder = () => (
		<section className="no-scrollbar -mx-4 flex select-none gap-3.5 overflow-x-auto px-4 md:mx-0 md:px-0 lg:gap-4">
			{[...Array(3)].map((_, index) => (
				<article
					key={index}
					className="relative min-w-[280px] shrink-0 overflow-hidden rounded-2xl border border-neutral-800/50 bg-gradient-to-br from-neutral-900/90 to-neutral-925/90 p-5 md:min-w-0 md:flex-1 3xl:p-7"
				>
					<div className="animate-pulse space-y-3 3xl:space-y-4">
						<div className="size-10 rounded-xl bg-neutral-800 3xl:size-12"></div>
						<div className="space-y-2">
							<div className="h-3 w-24 rounded bg-neutral-800"></div>
							<div className="h-9 w-32 rounded bg-neutral-800"></div>
						</div>
					</div>
				</article>
			))}
		</section>
	);

	return (
		<div className="relative p-4 md:p-[1.25rem]">
			<div className="flex select-none flex-col gap-4 md:flex-row md:items-center md:justify-between">
					<div>
						<p className="text-lg text-neutral-300 3xl:text-xl">¡Bienvenido! 👋</p>
						<strong className="text-2xl text-neutral-0 3xl:text-3xl">{store_name}</strong>
					</div>
					{testAccounts.includes(email) && (
						<button
							className="absolute right-6 top-0 mt-6 rounded-lg bg-primary-500/20 px-4 py-2 text-primary-400"
							onClick={() => {
								setUpdateRegistrationStepsPayload({
									master_store_email: master_store.id,
									registration_steps: {
										company_info: false,
										bank_created: false,
										sucursal_created: false,
										registration_completed: false,
										terms_accepted: false
									}
								});
								setUpdateRegistrationSteps(true);
							}}
						>
							Probar Onboarding
						</button>
					)}
				{showStoreHealth && (
					<div className="w-full md:w-auto">
						<StoreHealthCard
							summary={storeHealthSummary || undefined}
							loading={storeHealthLoading}
							error={!!storeHealthError}
							isMaster={master_user === true}
						/>
					</div>
				)}
			</div>

			{/* Inactive Account Indicator */}
			{isInactive && (
				<div className="mb-6 mt-4 rounded-lg border border-orange-500/30 bg-orange-500/10 p-4 light:border-orange-600/40 light:bg-orange-50">
					<div className="flex items-center gap-3">
						<AlertCircle className="size-5 flex-shrink-0 text-orange-400 light:text-orange-600" />
						<div className="space-y-1">
							<h3 className="text-sm font-semibold text-orange-200 light:text-orange-800">Cuenta Inactiva</h3>
							<p className="text-sm text-orange-300/90 light:text-orange-700">
								Su cuenta está actualmente inactiva. Algunas funcionalidades están deshabilitadas. Contacte al equipo de soporte
								para reactivar su cuenta.
							</p>
						</div>
					</div>
				</div>
			)}

			<div className="my-6 space-y-5 md:my-8 md:space-y-6">
				<FilterBar />
				{loading ? <AnalyticsCardsPlaceholder /> : data && <AnalyticsCards />}
			</div>

			<SummaryTables key="summary-tables" userData={userData!} internal={internal} selectedBranch={selectedBranch} />
		</div>
	);
}
